/**
 * PartnerCompaniesTab Component (T029 - US1)
 * Partner companies management interface with CRUD operations
 *
 * Features:
 * - List partner companies with search and pagination
 * - Create new partner company
 * - Edit existing partner company
 * - Toggle isActive status
 * - Display referral codes
 * - Loading and error states
 */

import { useState, useEffect } from 'react';
import { Plus, AlertCircle, Search, Edit2, Building2 } from 'lucide-react';
import { PartnerCompanyForm } from './PartnerCompanyForm';
import { PaginationControls } from './PaginationControls';
import { usePagination } from '../hooks/usePagination';
import {
  usePartnerCompanies,
  useCreatePartnerCompany,
  useUpdatePartnerCompany,
} from '@/features/partner-companies/hooks/usePartnerCompanies';
import type {
  PartnerCompany,
  CreatePartnerCompanyPayload,
  UpdatePartnerCompanyPayload,
} from '@/features/partner-companies/types/partner-company.types';

type FormMode = 'create' | 'edit' | null;

/**
 * Debounce delay for search input (in milliseconds)
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Custom hook for debounced value
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function PartnerCompaniesTab() {
  // State management
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [selectedCompany, setSelectedCompany] = useState<
    PartnerCompany | undefined
  >();
  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | ''>('');

  // Debounce search to prevent excessive API calls
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  // Pagination
  const pagination = usePagination({
    initialPage: 1,
    initialPageSize: 20,
  });

  // Data fetching (uses debounced search to prevent rate limiting)
  const { data, isLoading, error, refetch } = usePartnerCompanies({
    page: pagination.page,
    limit: pagination.pageSize,
    search: debouncedSearch || undefined,
    isActive: isActiveFilter === '' ? undefined : isActiveFilter,
  });

  // Mutations
  const createMutation = useCreatePartnerCompany();
  const updateMutation = useUpdatePartnerCompany();

  // Handlers
  const handleCreate = () => {
    setSelectedCompany(undefined);
    setFormMode('create');
  };

  const handleEdit = (company: PartnerCompany) => {
    setSelectedCompany(company);
    setFormMode('edit');
  };

  const handleFormSubmit = async (
    payload: CreatePartnerCompanyPayload | UpdatePartnerCompanyPayload,
  ) => {
    if (formMode === 'create') {
      await createMutation.mutateAsync(payload as CreatePartnerCompanyPayload);
    } else if (formMode === 'edit' && selectedCompany) {
      await updateMutation.mutateAsync({
        id: selectedCompany.id,
        payload: payload as UpdatePartnerCompanyPayload,
      });
    }
    setFormMode(null);
    setSelectedCompany(undefined);
  };

  const handleFormCancel = () => {
    setFormMode(null);
    setSelectedCompany(undefined);
  };

  const handleToggleActive = async (company: PartnerCompany) => {
    await updateMutation.mutateAsync({
      id: company.id,
      payload: { isActive: !company.isActive },
    });
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    pagination.goToFirstPage();
  };

  const handleFilterChange = (value: string) => {
    setIsActiveFilter(value === '' ? '' : value === 'active');
    pagination.goToFirstPage();
  };

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Failed to load partner companies
        </h3>
        <p className="text-gray-600 mb-4">{error.message}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="px-4 py-2 bg-[#026857] text-white rounded-md hover:bg-[#025246] transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Partner Companies
          </h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Manage partner company affiliations
          </p>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-[#026857] text-white rounded-md hover:bg-[#025246] transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create Partner Company
        </button>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by name or referral code..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor="statusFilter" className="sr-only">
            Status
          </label>
          <select
            id="statusFilter"
            aria-label="Status"
            value={isActiveFilter === '' ? '' : isActiveFilter ? 'active' : 'inactive'}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        {data && (
          <span className="text-sm text-gray-600">
            {data.meta.total} partner compan{data.meta.total === 1 ? 'y' : 'ies'}
          </span>
        )}
      </div>

      {/* Table content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {isLoading ? (
          <PartnerCompaniesSkeleton />
        ) : data && data.data.length > 0 ? (
          <table className="w-full" role="table">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Name
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Referral Code
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Status
                </th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((company) => (
                <tr
                  key={company.id}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      {company.logoUrl ? (
                        <img
                          src={company.logoUrl}
                          alt={`${company.name} logo`}
                          className="h-10 w-10 rounded-md object-cover"
                        />
                      ) : (
                        <div
                          data-testid={`company-placeholder-${company.id}`}
                          className="h-10 w-10 rounded-md bg-gray-200 flex items-center justify-center"
                        >
                          <Building2 className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <span className="font-medium text-gray-900">
                        {company.name}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">
                      {company.referralCode}
                    </code>
                  </td>
                  <td className="py-3 px-4">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={company.isActive}
                      onClick={() => handleToggleActive(company)}
                      disabled={updateMutation.isPending}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                        company.isActive ? 'bg-[#026857]' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          company.isActive ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="ml-2 text-sm text-gray-600">
                      {company.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      type="button"
                      onClick={() => handleEdit(company)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                      aria-label={`Edit ${company.name}`}
                    >
                      <Edit2 className="h-4 w-4" />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Building2 className="h-12 w-12 mb-4" />
            <p>No partner companies found</p>
            {(search || isActiveFilter !== '') && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setIsActiveFilter('');
                }}
                className="mt-2 text-[#026857] hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && data.meta.totalPages > 1 && (
        <PaginationControls
          meta={{
            page: data.meta.page,
            pageSize: data.meta.limit,
            total: data.meta.total,
            totalPages: data.meta.totalPages,
            hasNextPage: data.meta.page < data.meta.totalPages,
            hasPreviousPage: data.meta.page > 1,
          }}
          onPageChange={pagination.setPage}
          onPageSizeChange={pagination.setPageSize}
        />
      )}

      {/* Form modal */}
      {formMode && (
        <PartnerCompanyForm
          partnerCompany={selectedCompany}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}

/**
 * Loading skeleton for partner companies table
 */
function PartnerCompaniesSkeleton() {
  return (
    <div data-testid="partner-companies-skeleton" className="space-y-4">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 py-3 px-4 animate-pulse"
        >
          <div className="h-10 w-10 rounded-md bg-gray-200" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-48 bg-gray-200 rounded" />
            <div className="h-3 w-24 bg-gray-200 rounded" />
          </div>
          <div className="h-6 w-11 bg-gray-200 rounded-full" />
          <div className="h-8 w-16 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
  );
}
